import {
  AUDIO_GAINS, HIT_SOUND_MAX_AUDIBLE_M, HIT_INTENSITY_DAMAGE_NORMALIZER, HIT_INTENSITY_MIN, FOOTSTEP_TIMING,
  LEVEL_UP_FANFARE, ABILITY_CAST_PROFILES, ABILITY_SOUND_MAX_AUDIBLE_M, SFX, SFX_GAINS,
  ALL_SAMPLE_URLS, FOOTSTEP_SAMPLES, COMBAT_SAMPLES, COMBAT_GAINS, SAMPLE_PITCH_JITTER,
  REMOTE_FOOTSTEP_MAX_AUDIBLE_M, ABILITY_CAST_SAMPLES, UI_SAMPLES, OUTCOME_SAMPLES, UI_REJECT_MIN_INTERVAL_MS,
} from '../config/audio-tuning';
import { computeSpatialParams, Vec2 } from './spatial-audio';
import { FootstepTracker } from './footstep-tracker';
import { playNoiseBurst, playTone } from './synth';
import { AmbientBed } from './ambient-bed';
import { MusicPlayer } from './music-player';
import { SampleBank } from './sample-bank';
import { isMusicEnabled, setMusicEnabled } from './music-preference';

/**
 * The five buses a player can actually move, as fractions of unity. Kept here
 * rather than imported from the settings UI so the audio layer does not depend
 * on a screen — `ui/settings` pushes into this, never the other way round.
 */
export interface AudioMix {
  master: number;
  sfx: number;
  ambient: number;
  ui: number;
  music: number;
}

/** The mix the game was authored at. Same numbers as `AUDIO_GAINS`. */
export const DEFAULT_MIX: AudioMix = {
  master: AUDIO_GAINS.MASTER,
  sfx: AUDIO_GAINS.SFX,
  ambient: AUDIO_GAINS.AMBIENT,
  ui: AUDIO_GAINS.UI,
  music: AUDIO_GAINS.MUSIC,
};

/** melee-combat.md's weapon damage table (LIGHT 18 .. HEAVY 52, before riposte/crit/talent multipliers) mapped to a 0-1 loudness/weight scale for `playHitImpact`. */
export function computeHitIntensity(damage: number): number {
  return Math.min(1, Math.max(HIT_INTENSITY_MIN, damage / HIT_INTENSITY_DAMAGE_NORMALIZER));
}

/**
 * Game Feel Polish Phase 3 — one instance per mounted game view (created and
 * disposed alongside `dungeon-instance-bootstrap.ts`/`arena-bootstrap.ts`'s
 * own teardown), not a module-level singleton: a stratum descend tears down
 * and remounts the whole bootstrap, and each mount should get a fresh
 * AudioContext rather than accumulate one per remount.
 *
 * Every sound in the game is synthesized at runtime (`synth.ts`) — there are
 * no audio asset files anywhere in this repo. All playback methods below are
 * no-ops before `ensureStarted()` has actually created the context (e.g. a
 * hit lands before the first user gesture) — they degrade silently rather
 * than throwing, same precedent as `character-visual.ts`'s asset-load
 * failure handling.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private uiGain: GainNode | null = null;
  private removeGestureListeners: (() => void) | null = null;
  private listenerPos: Vec2 = { x: 0, z: 0 };
  private listenerYawRad = 0;
  private ambientBed: AmbientBed | null = null;
  private musicGain: GainNode | null = null;
  private musicPlayer: MusicPlayer | null = null;
  /**
   * The bed this screen wants, whether or not it is audible right now. Kept so
   * that switching music back on (from any control) can restart it without
   * the caller having to know which track its screen uses.
   */
  private wantedMusicUrl: string | null = null;
  /** A play() is fetching/decoding; a second one would restart the bed when both land. */
  private musicPending = false;
  private samples: SampleBank | null = null;
  private readonly localFootsteps = new FootstepTracker(FOOTSTEP_TIMING);
  private lastRejectAtMs = 0;

  /**
   * The player's own mix, from the settings screen.
   *
   * Held rather than read on demand because the context is created lazily, on
   * the first gesture — a volume set before that has nowhere to go yet, and
   * would otherwise be lost. `applyMix` writes whatever is here into the buses
   * as soon as they exist, and `setMix` writes through when they already do.
   */
  private mix: AudioMix = { ...DEFAULT_MIX };

  /**
   * Lazily creates the AudioContext + bus graph and arms a one-time
   * resume-on-gesture listener — browsers suspend/block audio until a real
   * user gesture. Safe to call every frame or on every input event; it's a
   * cheap no-op once started. `AudioContext` may not exist at all in some
   * test/CI environments (jsdom) — that's treated the same as "no user
   * gesture yet," everything downstream just stays a silent no-op.
   */
  ensureStarted(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const AudioContextCtor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    const ctx = new AudioContextCtor();
    this.ctx = ctx;
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = this.mix.master;
    this.masterGain.connect(ctx.destination);

    this.sfxGain = ctx.createGain();
    this.sfxGain.gain.value = this.mix.sfx;
    this.sfxGain.connect(this.masterGain);

    this.ambientGain = ctx.createGain();
    this.ambientGain.gain.value = this.mix.ambient;
    this.ambientGain.connect(this.masterGain);

    this.uiGain = ctx.createGain();
    this.uiGain.gain.value = this.mix.ui;
    this.uiGain.connect(this.masterGain);

    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = this.mix.music;
    this.musicGain.connect(this.masterGain);
    this.musicPlayer = new MusicPlayer(ctx, this.musicGain);

    // Cues ride the SFX bus, not the UI bus: most of them are events at a
    // position in the world. The unspatialized ones (the level-up) pass no pan
    // and simply do not get a panner node.
    this.samples = new SampleBank(ctx, this.sfxGain);
    this.samples.preload(ALL_SAMPLE_URLS);

    const resume = () => void ctx.resume();
    const gestureEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
    for (const evt of gestureEvents) window.addEventListener(evt, resume, { passive: true });
    this.removeGestureListeners = () => { for (const evt of gestureEvents) window.removeEventListener(evt, resume); };
  }

  /** Called once per frame with the camera's current world position + yaw — every spatialized sound played before the next call uses this pose. */
  setListenerPose(position: Vec2, yawRad: number): void {
    this.listenerPos = position;
    this.listenerYawRad = yawRad;
  }

  /**
   * A blow landing on a body.
   *
   * `damage` drives loudness *and* pitch: a heavier hit plays lower, which is
   * how a sample set of three takes covers the whole weapon table without
   * needing one recording per weight. That scaling is the information the
   * recording cannot carry on its own, and it is why this is not simply
   * `playVariant`.
   *
   * The single impact path for the entire game — `playCombatOutcome` delegates
   * here for a landed hit rather than playing its own, so a swing cannot
   * produce two impacts stacked on each other.
   */
  playHitImpact(sourcePos: Vec2, damage: number, targetIsBone = false): void {
    if (!this.samples) return;
    const { pan, gain01 } = computeSpatialParams(this.listenerPos, this.listenerYawRad, sourcePos, HIT_SOUND_MAX_AUDIBLE_M);
    if (gain01 <= 0) return;

    const intensity = computeHitIntensity(damage);
    const group = targetIsBone ? COMBAT_SAMPLES.impactBone : COMBAT_SAMPLES.impactFlesh;
    this.samples.playVariant(group, {
      // A light tap is audibly a tap; a heavy blow is most of the way to full.
      gain: COMBAT_GAINS.IMPACT * gain01 * (0.5 + 0.5 * intensity),
      pan,
      // Heavier reads lower. Range is narrow on purpose — past about a
      // semitone either way a sample stops sounding like the same object.
      rate: (1.06 - 0.12 * intensity) * (1 + (Math.random() - 0.5) * SAMPLE_PITCH_JITTER),
    });
  }

  playAbilityCast(sourcePos: Vec2, profile: string): void {
    if (!this.ctx || !this.sfxGain) return;
    const { pan, gain01 } = computeSpatialParams(this.listenerPos, this.listenerYawRad, sourcePos, ABILITY_SOUND_MAX_AUDIBLE_M);
    if (gain01 <= 0) return;

    const group = ABILITY_CAST_SAMPLES[profile];
    if (group && this.samples?.playVariant(group, {
      gain: COMBAT_GAINS.ABILITY_CAST * gain01, pan, rate: this.jitteredRate(),
    })) return;

    // Fallback while the bank is still decoding, or for a profile with no
    // recording yet — the oscillator pair this cue used to be.
    const spec = ABILITY_CAST_PROFILES[profile];
    if (!spec) return;
    playTone(this.ctx, this.sfxGain, {
      freq: spec.tone.freq,
      sweepToHz: spec.tone.sweepHz,
      type: spec.tone.type,
      envelope: { attackS: 0.004, decayS: spec.tone.decayS, peakGain: spec.tone.peakGain * gain01 },
      pan,
    });
    playNoiseBurst(this.ctx, this.sfxGain, {
      durationS: spec.noise.durationS,
      filterType: spec.noise.filterType,
      filterFreq: spec.noise.filterFreq,
      envelope: { attackS: 0.004, decayS: spec.noise.decayS, peakGain: spec.noise.peakGain * gain01 },
      pan,
    });
  }

  /**
   * Your own feet.
   *
   * The same recorded banks the rest of the floor walks on, but not
   * spatialized: your feet are always at the camera, so a distance falloff has
   * nothing to compute and a fixed small left/right pan is what actually reads
   * as alternating steps. `FootstepTracker` decides WHEN; this decides what it
   * sounds like.
   *
   * `stratumDepth` defaults to the Cellars so the arena — which has no strata —
   * can call this unchanged.
   */
  updateLocalFootsteps(dtSeconds: number, isMoving: boolean, isSprinting: boolean, stratumDepth = 1): void {
    const foot = this.localFootsteps.advance(dtSeconds, isMoving, isSprinting);
    if (!foot || !this.ctx || !this.sfxGain) return;

    const group = FOOTSTEP_SAMPLES[stratumDepth] ?? FOOTSTEP_SAMPLES[1];
    const gain = COMBAT_GAINS.FOOTSTEP * (isSprinting ? COMBAT_GAINS.FOOTSTEP_SPRINT_MULTIPLIER : 1);
    if (this.samples?.playVariant(group, {
      gain, pan: foot === 'left' ? -0.15 : 0.15, rate: this.jitteredRate(),
    })) return;

    // Fallback for the first steps of a run, before the bank has decoded.
    const pitchVariance = 1 + (Math.random() - 0.5) * 0.3;
    playNoiseBurst(this.ctx, this.sfxGain, {
      durationS: 0.06,
      filterType: 'lowpass',
      filterFreq: 500,
      envelope: { attackS: 0.002, decayS: 0.05, peakGain: 0.18 },
      playbackRate: pitchVariance,
      pan: foot === 'left' ? -0.15 : 0.15,
    });
  }

  /** Shared by every unspatialized UI cue: recorded take first, oscillator only if it is not ready. */
  private playUiSample(group: readonly string[]): boolean {
    return this.samples?.playVariant(group, { gain: COMBAT_GAINS.UI, rate: this.jitteredRate() }) ?? false;
  }

  playUiReject(): void {
    if (!this.ctx || !this.uiGain) return;
    // Throttled, unlike every other cue: this one fires once per keypress, and
    // a player mashing a cooldown produced 118 of them in a minute of live
    // play. See UI_REJECT_MIN_INTERVAL_MS.
    const now = performance.now();
    if (now - this.lastRejectAtMs < UI_REJECT_MIN_INTERVAL_MS) return;
    this.lastRejectAtMs = now;
    if (this.playUiSample(UI_SAMPLES.REJECT)) return;
    playTone(this.ctx, this.uiGain, { freq: 180, type: 'square', envelope: { attackS: 0.001, decayS: 0.12, peakGain: 0.25 } });
  }

  /** Two-note ascending chime — the classic "pickup" shape. Both notes are scheduled via Web Audio's own clock (`delayS`), not a JS timer, so the second note's timing survives main-thread jank. */
  playUiLoot(): void {
    if (!this.ctx || !this.uiGain) return;
    if (this.playUiSample(UI_SAMPLES.LOOT)) return;
    playTone(this.ctx, this.uiGain, { freq: 660, type: 'sine', envelope: { attackS: 0.001, decayS: 0.08, peakGain: 0.2 } });
    playTone(this.ctx, this.uiGain, { freq: 990, type: 'sine', envelope: { attackS: 0.001, decayS: 0.1, peakGain: 0.18 }, delayS: 0.06 });
  }

  playRunEnd(outcome: 'EXTRACTED' | 'DIED'): void {
    if (this.samples?.play(OUTCOME_SAMPLES[outcome], { gain: COMBAT_GAINS.OUTCOME })) return;
    if (!this.ctx || !this.uiGain) return;
    if (outcome === 'EXTRACTED') {
      playTone(this.ctx, this.uiGain, { freq: 440, type: 'triangle', envelope: { attackS: 0.005, decayS: 0.3, peakGain: 0.3 } });
      playTone(this.ctx, this.uiGain, { freq: 660, type: 'triangle', envelope: { attackS: 0.005, decayS: 0.35, peakGain: 0.25 }, delayS: 0.09 });
    } else {
      playTone(this.ctx, this.uiGain, { freq: 160, type: 'sawtooth', envelope: { attackS: 0.01, decayS: 0.5, peakGain: 0.3 } });
    }
  }

  /** Arena's WIN/DRAW/LOSE personal outcome (`arena-round-overlay.ts`'s `ArenaPersonalOutcome`) — a distinct mapping from `playRunEnd`'s EXTRACTED/DIED since a PvP round loss isn't the same event as a dungeon-run death. */
  /** A jittered playback rate, so repeated takes never land on the same pitch twice. */
  private jitteredRate(): number {
    return 1 + (Math.random() - 0.5) * 2 * SAMPLE_PITCH_JITTER;
  }

  /**
   * A footstep from another body in the world.
   *
   * Spatialized, unlike `updateLocalFootsteps` — the whole point is *where*
   * they are. Falls silent (rather than falling back to synthesis) when the
   * bank is not ready or the source is out of range: a footstep is the most
   * frequently played sound in the game, and a synthesized stand-in firing
   * hundreds of times per minute would be worse than the silence it replaced.
   *
   * `stratumDepth` picks the material — 1=Cellars, 2=Ossuary, 3=Vaults, the same
   * convention as `AMBIENT_PROFILES`.
   */
  playRemoteFootstep(sourcePos: Vec2, stratumDepth: number, isSprinting: boolean, gainMultiplier = 1): void {
    if (!this.samples) return;
    const group = FOOTSTEP_SAMPLES[stratumDepth] ?? FOOTSTEP_SAMPLES[1];
    const { pan, gain01 } = computeSpatialParams(
      this.listenerPos, this.listenerYawRad, sourcePos, REMOTE_FOOTSTEP_MAX_AUDIBLE_M);
    if (gain01 <= 0) return;
    const gain = COMBAT_GAINS.FOOTSTEP * gain01 * gainMultiplier
      * (isSprinting ? COMBAT_GAINS.FOOTSTEP_SPRINT_MULTIPLIER : 1);
    this.samples.playVariant(group, { gain, pan, rate: this.jitteredRate() });
  }

  /**
   * The sound a resolved swing makes, whatever it did.
   *
   * `combat-resolver.ts` resolves five outcomes and two extra flags; until this
   * existed only `hit` made any sound. The mapping is deliberately not uniform:
   *
   * - **hit** keeps `playHitImpact`'s damage-scaled synthesis *underneath* a
   *   recorded impact, because the synthesis carries information the sample
   *   cannot — how hard the blow was. The sample gives it a body; the layer
   *   underneath still says how much it hurt.
   * - **riposte** replaces the normal impact rather than adding to it. It is a
   *   different event, not a louder one, and the player needs to know a counter
   *   landed without reading the log.
   * - **stagger** plays over whatever else happened, since it is a consequence
   *   of the blow rather than an alternative to it.
   */
  playCombatOutcome(
    sourcePos: Vec2,
    outcome: string,
    options: { damage?: number; isRiposte?: boolean; staggers?: boolean; targetIsBone?: boolean } = {},
  ): void {
    if (!this.samples) return;
    const { pan, gain01 } = computeSpatialParams(
      this.listenerPos, this.listenerYawRad, sourcePos, HIT_SOUND_MAX_AUDIBLE_M);
    if (gain01 <= 0) return;
    const place = (group: readonly string[], gain: number) =>
      this.samples?.playVariant(group, { gain: gain * gain01, pan, rate: this.jitteredRate() });

    switch (outcome) {
      case 'parried': place(COMBAT_SAMPLES.parried, COMBAT_GAINS.PARRY); break;
      case 'blocked': place(COMBAT_SAMPLES.blocked, COMBAT_GAINS.BLOCK); break;
      case 'dodged': place(COMBAT_SAMPLES.dodged, COMBAT_GAINS.DODGE); break;
      case 'miss': place(COMBAT_SAMPLES.miss, COMBAT_GAINS.MISS); break;
      case 'hit':
        if (options.isRiposte) {
          place(COMBAT_SAMPLES.riposte, COMBAT_GAINS.RIPOSTE);
        } else {
          // Delegated rather than duplicated: `playHitImpact` owns damage
          // scaling, and it is also the path ability damage takes (which never
          // reaches combat.result). Two impact paths would drift apart.
          this.playHitImpact(sourcePos, options.damage ?? 0, options.targetIsBone);
        }
        break;
      default: break; // an outcome this build does not know is silent, not a crash
    }

    if (options.staggers) place(COMBAT_SAMPLES.stagger, COMBAT_GAINS.STAGGER);
  }

  /**
   * The level-up fanfare (`LEVEL_UP_FANFARE`).
   *
   * On the UI bus rather than the SFX bus, and unspatialized: this is news
   * about *you*, not an event at a position in the world — it should not
   * quieten because the camera happens to be facing away from your own body,
   * the way `playHitImpact` correctly does.
   */
  playLevelUp(): void {
    if (!this.ctx || !this.uiGain) return;
    // The recorded cue when it is ready, the oscillator fanfare when it is not
    // — a level-up during the first seconds of a run, before the sample has
    // decoded, still sounds like something. See `SampleBank.play`'s contract.
    if (this.samples?.play(SFX.LEVEL_UP, { gain: SFX_GAINS[SFX.LEVEL_UP] })) return;
    const f = LEVEL_UP_FANFARE;
    f.ARPEGGIO_HZ.forEach((freq, i) => {
      playTone(this.ctx!, this.uiGain!, {
        freq, type: 'triangle', delayS: i * f.STEP_S,
        envelope: { attackS: 0.004, decayS: f.NOTE_DECAY_S, peakGain: f.NOTE_PEAK_GAIN },
      });
    });
    for (const freq of f.HOLD_HZ) {
      playTone(this.ctx, this.uiGain, {
        freq, type: 'sine',
        envelope: { attackS: 0.02, decayS: f.HOLD_DECAY_S, peakGain: f.HOLD_PEAK_GAIN },
      });
    }
    playTone(this.ctx, this.uiGain, {
      freq: f.SPARKLE_HZ, type: 'sine', delayS: f.SPARKLE_DELAY_S,
      envelope: { attackS: 0.05, decayS: f.SPARKLE_DECAY_S, peakGain: f.SPARKLE_PEAK_GAIN },
    });
  }

  playRoundEnd(outcome: 'WIN' | 'DRAW' | 'LOSE'): void {
    if (this.samples?.play(OUTCOME_SAMPLES[outcome], { gain: COMBAT_GAINS.OUTCOME })) return;
    if (!this.ctx || !this.uiGain) return;
    if (outcome === 'WIN') {
      playTone(this.ctx, this.uiGain, { freq: 440, type: 'triangle', envelope: { attackS: 0.005, decayS: 0.3, peakGain: 0.3 } });
      playTone(this.ctx, this.uiGain, { freq: 660, type: 'triangle', envelope: { attackS: 0.005, decayS: 0.35, peakGain: 0.25 }, delayS: 0.09 });
    } else if (outcome === 'DRAW') {
      playTone(this.ctx, this.uiGain, { freq: 300, type: 'triangle', envelope: { attackS: 0.01, decayS: 0.4, peakGain: 0.25 } });
    } else {
      playTone(this.ctx, this.uiGain, { freq: 160, type: 'sawtooth', envelope: { attackS: 0.01, decayS: 0.5, peakGain: 0.3 } });
    }
  }

  /**
   * Start (or switch to) a looping music bed. No-op without Web Audio, and
   * no-op — importantly, without fetching the track — when the player has
   * music turned off.
   *
   * Returns a promise only so callers *may* await the fetch/decode; the screens
   * that mount music do not, because a bed is not worth delaying a render for.
   */
  async playMusic(url: string): Promise<void> {
    this.wantedMusicUrl = url;
    if (!isMusicEnabled()) return;
    this.ensureStarted();
    this.musicPending = true;
    try {
      await this.musicPlayer?.play(url);
    } finally {
      this.musicPending = false;
    }
    // The fetch + decode can take seconds; music switched off meanwhile must
    // not start the moment the buffer lands.
    if (!isMusicEnabled()) await this.musicPlayer?.stop();
  }

  /** Fade out and release the current bed. Safe when nothing is playing. */
  async stopMusic(): Promise<void> {
    this.wantedMusicUrl = null;
    await this.musicPlayer?.stop();
  }

  /**
   * Follow the stored preference without writing it — the settings store
   * already has (see `bindAudioToSettings`). Idempotent, so it is safe to call
   * on every settings emission: play() of the mounted track and stop() of a
   * silent player are both no-ops.
   */
  applyMusicEnabled(enabled: boolean): void {
    if (!enabled) {
      void this.musicPlayer?.stop();
      return;
    }
    // Already sounding or on its way: the settings store emits on every slider
    // move, and a re-issued play() racing the first fetch restarts the bed.
    if (!this.wantedMusicUrl || this.musicPending || this.musicPlayer?.playingUrl === this.wantedMusicUrl) return;
    void this.playMusic(this.wantedMusicUrl);
  }

  /**
   * Apply a music on/off change immediately.
   *
   * Turning it back on has to re-issue the track, because a disabled
   * `playMusic` never fetched one — so the caller passes the bed the current
   * screen wants, and this decides whether it plays.
   */
  async setMusicEnabled(enabled: boolean, currentTrackUrl: string | null): Promise<void> {
    setMusicEnabled(enabled);
    if (!enabled) {
      await this.stopMusic();
      return;
    }
    if (currentTrackUrl) await this.playMusic(currentTrackUrl);
  }

  /** design/gdd/stratum.md's 1=Cellars/2=Ossuary/3=Vaults depth convention — see `audio-tuning.ts`'s `AMBIENT_PROFILES`. Call once per stratum join; a stratum change remounts the whole bootstrap, so there's no live "switch profile" case. */
  startAmbient(stratumDepth: number): void {
    if (!this.ctx || !this.ambientGain) return;
    this.ambientBed?.stop();
    this.ambientBed = new AmbientBed(this.ctx, this.ambientGain, this.samples ?? undefined);
    this.ambientBed.start(stratumDepth);
  }

  stopAmbient(): void {
    this.ambientBed?.stop();
    this.ambientBed = null;
  }

  /**
   * Apply the player's mix. Safe before the context exists: the values are
   * remembered and written into the buses the moment `ensureStarted` builds
   * them, so a slider moved in the hub is already correct on the first frame of
   * the run.
   */
  setMix(mix: Partial<AudioMix>): void {
    this.mix = { ...this.mix, ...mix };
    const buses: Array<[GainNode | null, number]> = [
      [this.masterGain, this.mix.master],
      [this.sfxGain, this.mix.sfx],
      [this.ambientGain, this.mix.ambient],
      [this.uiGain, this.mix.ui],
      [this.musicGain, this.mix.music],
    ];
    for (const [node, value] of buses) {
      if (!node || !this.ctx) continue;
      // A ramp, not a jump: setting `gain.value` mid-drag clicks audibly on
      // anything already sounding.
      node.gain.setTargetAtTime(value, this.ctx.currentTime, 0.02);
    }
  }

    dispose(): void {
    this.stopAmbient();
    this.musicPlayer?.dispose();
    this.musicPlayer = null;
    this.samples?.dispose();
    this.samples = null;
    this.musicGain = null;
    this.removeGestureListeners?.();
    this.removeGestureListeners = null;
    void this.ctx?.close();
    this.ctx = null;
  }
}
